import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  List,
  Paper,
  Divider,
  ListItem,
  ListItemText,
  ListSubheader,
} from '@material-ui/core';
import Can from 'components/Can';
import { useStyles } from './CalendarWidget.styled';
import i18n from 'i18n-js';

/**
 * Selected item on noc calendar
 */
const SelectedItemPopup = ({
  item,
  position,
  enableSubscription,
  requestType,
  handleClose,
  goToReservation,
  handleChangeReservationTimes,
  handleChangeVehicleOnReservation,
}) => {
  const classes = useStyles();
  const ref = useRef(null);

  useEffect(() => {
    /**
     * Alert if clicked on outside of element and close the popup
     */
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        handleClose();
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, handleClose]);

  return (
    <Box ref={ref} zIndex={1000} position="fixed" top={position.y + 20} left={position.x - 20}>
      <Paper elevation={3} className={classes.itemPopup}>
        <List
          component="nav"
          aria-labelledby="nested-list-subheader"
          subheader={
            <ListSubheader
              className={classes.popupTitle}
              component="div"
              id="nested-list-subheader"
            >
              {`${item.id} - ${item?.user?.name || item?.user?.first_name || ''}`}
            </ListSubheader>
          }
          className={classes.root}
        >
          {requestType !== 'on_demand' &&
            !enableSubscription &&
            !['COMPLETED'].includes(item.status) && (
              <Can I="update_vehicle" a="reservation">
                <ListItem
                  button
                  onClick={() => {
                    handleChangeVehicleOnReservation(item);
                    handleClose();
                  }}
                >
                  <ListItemText
                    classes={{ primary: classes.listItem }}
                    primary={i18n.t('dashboard.noc_change_vehicle')}
                  />
                </ListItem>
              </Can>
            )}
          {requestType !== 'on_demand' &&
            !enableSubscription &&
            !['COMPLETED'].includes(item.status) && (
              <Can I="update_times" a="reservation">
                <ListItem
                  button
                  onClick={() => {
                    handleChangeReservationTimes(item);
                    handleClose();
                  }}
                >
                  <ListItemText
                    classes={{
                      primary: classes.listItem,
                    }}
                    primary={i18n.t('dashboard.noc_change_times')}
                  />
                </ListItem>
              </Can>
            )}
          {enableSubscription ? (
            <Can I="update_times" a="reservation">
              <ListItem
                button
                onClick={() => {
                  handleClose();
                }}
              >
                <ListItemText
                  classes={{
                    primary: classes.listItem,
                  }}
                  primary={i18n.t('dashboard.noc_go_to_subscription')}
                />
              </ListItem>
            </Can>
          ) : (
            item.hasOwnProperty('status') &&
            !['OFFLINE'].includes(item.status) && (
              <>
                <Divider />
                <Can I="read" a="reservation">
                  <ListItem
                    button
                    onClick={() => {
                      goToReservation(item);
                    }}
                  >
                    <ListItemText
                      classes={{
                        primary: classes.listItem,
                      }}
                      primary={i18n.t('dashboard.noc_go_to_reservation')}
                    />
                  </ListItem>
                </Can>
              </>
            )
          )}
        </List>
      </Paper>
      <Box className={classes.itemPopupContainer}>
        <Box className={classes.itemPopupArrow} />
      </Box>
    </Box>
  );
};

SelectedItemPopup.propTypes = {
  item: PropTypes.object.isRequired,
  position: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  requestType: PropTypes.string.isRequired,
  goToReservation: PropTypes.func.isRequired,
  enableSubscription: PropTypes.bool.isRequired,
};

SelectedItemPopup.defaultProps = {
  requestType: '',
  handleClose: () => {},
  goToReservation: () => {},
  enableSubscription: false,
  handleChangeReservationTimes: () => {},
};

export default SelectedItemPopup;
